import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {DeviceMeasurement} from '../../../../model/device-measurement';
import {MeasurementType} from '../../../../model/measurement-type';
import {Tenant} from '../../../../model/tenant';
import {TenantsService} from '../../../../services/tenants.service';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {DevicesService} from '../../../../services/devices.service';
import {Device} from '../../../../model/device';
import {ActionButton} from '../../../../model/ui/action-button';
import {noSpecialCharacters} from '../../../../shared/custom-validators';
import {PreviousRouteService} from '../../../../services/previous-route.service';
import {DefaultMeasurementType} from "../../../../model/default-measurement-type";
import {FileData, FileErrors} from "../../../../model/import-csv/file-data";
import {CommonMessagesService} from "../../../../services/common-messages.service";
import {CsvMeasurementData} from "../../../../model/import-csv/csv-measurement-data";
import {CsvErrorList} from "../../../../model/import-csv/csv-error";
import {TranslocoService} from "@ngneat/transloco";
import {MeasurementCsvErrors} from "../../../import-measurements-csv/measurement-csv-errors";
import {MeasurementImportCsv} from "../../../../model/import-csv/measurement-import-csv";

@Component({
  selector: 'app-devices-form-measurements',
  templateUrl: './devices-form-measurements.component.html',
  styleUrls: ['./devices-form-measurements.component.scss']
})
export class DevicesFormMeasurementsComponent implements OnInit {

  @Input() mode: 'create' | 'edit';

  @Input() deviceMeasurements: DeviceMeasurement[];
  @Output() deviceMeasurementsChange = new EventEmitter<DeviceMeasurement[]>();
  @Output() previousStep = new EventEmitter<void>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<string>();

  @Input() tenant: Tenant;
  tenantIsEmpty: boolean;

  deviceMeasurementsForm: FormGroup;
  rowsForm: FormArray;

  measurementTypes: MeasurementType[];

  displayCopyFromDevice: boolean;
  copyFromDeviceActions: ActionButton[];

  copyDeviceMeasurements: DeviceMeasurement[];
  public rows = 10;
  public first = 0;
  public totalMeasures: number;

  public files: FileData[] = [];
  public filesUploaded: boolean = false;
  public fileExtensions: string[] = ['csv'];

  @ViewChild('fileInput') attachments: any;

  public fileFormatIncorrect: boolean = false;
  public measurements: CsvMeasurementData[] = [];
  public totalMeasurements: number;

  constructor(private tenantsService: TenantsService,
              private devicesService: DevicesService,
              private formBuilder: FormBuilder,
              private previousService: PreviousRouteService,
              private readonly commonMessageService: CommonMessagesService,
              private readonly i18n: TranslocoService) {
  }

  ngOnInit(): void {
    this.buildCopyFromDeviceActions();
    if (!this.tenant) {
      this.tenantIsEmpty = true;
    } else {
      this.loadAndCreateForm();
    }
  }

  private loadAndCreateForm(): void {
    this.tenantsService.getTenantMeasurementTypes(this.tenant.uuid).subscribe(response => {
      this.measurementTypes = response.measurementTypes
        .sort((a, b) => a.sensor > b.sensor ? 1 : -1);
      this.createForm(this.deviceMeasurements);
      this.copyDeviceMeasurements = this.deviceMeasurements;
    });
  }

  private createForm(deviceMeasurements: DeviceMeasurement[]): void {
    this.rowsForm = this.formBuilder.array(
      deviceMeasurements.map(measurement => this.formGroupFromDeviceMeasurement(measurement))
    );
    this.deviceMeasurementsForm = this.formBuilder.group({
      deviceMeasurements: this.rowsForm
    });
    this.totalMeasures = this.rowsForm.length;
  }

  addMeasurement(): void {
    const newMeasurement = this.formGroupFromDeviceMeasurement(new DeviceMeasurement());
    this.rowsForm.push(newMeasurement);
    this.totalMeasures = this.rowsForm.length;
    if ((this.first + this.rows) < this.totalMeasures) {
      this.first = this.rows * Math.floor(this.totalMeasures / this.rows);
    }
  }

  isDefault(rowIndex: number): boolean {
    return this.rowsForm.value[rowIndex].measurementType instanceof DefaultMeasurementType;
  }

  getMeasurementTypes(rowIndex: number): (MeasurementType | DefaultMeasurementType)[] {
    if (this.isDefault(rowIndex)) {
      return [this.rowsForm.value[rowIndex].measurementType];
    } else {
      return this.measurementTypes;
    }
  }

  onRowDelete(rowIndex: number): void {
    this.rowsForm.removeAt(rowIndex);
    this.totalMeasures = this.rowsForm.length;
  }

  deleteAllRows(): void {
    while (this.rowsForm.length > 0) {
      this.rowsForm.removeAt(0);
    }
    this.totalMeasures = 0;
  }

  onPreviousStep(): void {
    const measurements = this.deviceMeasurementsForm.get('deviceMeasurements').value;
    this.previousStep.emit();
    this.deviceMeasurementsChange.emit(measurements);
  }

  onNextStep(): void {
    if (this.deviceMeasurementsForm.valid) {
      const measurements = this.deviceMeasurementsForm.get('deviceMeasurements').value;
      measurements.forEach(sensor => {
        if (sensor.uuid == null) {
          sensor.uuid = this.copyDeviceMeasurements.find(copySensor => copySensor.name === sensor.name)?.uuid;
        }
      });
      this.nextStep.emit();
      this.deviceMeasurementsChange.emit(measurements);
    }
  }

  private formGroupFromDeviceMeasurement(measurement: DeviceMeasurement): FormGroup {
    return this.formBuilder.group({
      name: [measurement.name, [
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters
      ]],
      description: [measurement.description, [Validators.maxLength(255)]],
      alias: [measurement.alias],
      measurementType: [measurement.measurementType, [Validators.required]],
      sampleRate: [measurement.sampleRate, [Validators.min(1)]],
      expression: [measurement.expression],
      uuid: [measurement.uuid]
    }, {updateOn: 'blur'});
  }

  onCancel(): void {
    this.cancel.emit(this.previousService.getPreviousUrl());
  }

  private buildCopyFromDeviceActions(): void {
    this.copyFromDeviceActions = [
      {
        label: 'misc.copySensors',
        icon: 'pi pi-copy',
        action: (device: Device) => this.copyMeasurements(device)
      }, {
        label: 'misc.appendSensors',
        icon: 'pi pi-plus',
        action: (device: Device) => this.copyMeasurements(device, true)
      }
    ];
  }

  copyMeasurements(device: Device, append: boolean = false): void {
    this.devicesService.getDeviceMeasurementsOfDevice(device.uuid).subscribe(response => {
      if (!append) {
        this.deleteAllRows();
      }
      const measurements = this.deviceMeasurementsForm.get('deviceMeasurements').value;
      response.deviceMeasurements.forEach((measurement: DeviceMeasurement) => {
        measurement.uuid = this.copyDeviceMeasurements.find(copyMeasurement => copyMeasurement.name === measurement.name)?.uuid;
        if (measurements.find(sensor => sensor.name === measurement.name) === undefined) {
          this.rowsForm.push(this.formGroupFromDeviceMeasurement(measurement));
        }
      });
      this.totalMeasures += response.deviceMeasurements.length;
      this.displayCopyFromDevice = false;
    });
  }

  importMeasurements(importedMeasurements: CsvMeasurementData[]): void {
    const indexFounds: Array<number> = new Array<number>();
    const indexNotFounds: Array<number> = new Array<number>();
    for (let i = 0; i < this.rowsForm.controls.length; i++) {
      let found = false;
      for (let j = 0; j < importedMeasurements.length; j++) {
        if (this.rowsForm.controls[i].get('name').value === importedMeasurements[j].measurement.name) {
          found = true;
          indexFounds.push(j);
        }
      }
      if (!found) {
        indexNotFounds.push(i);
      }
    }
    indexFounds.sort((a, b) => a - b);
    indexFounds.reverse();
    indexFounds.forEach((index) => {
      importedMeasurements.splice(index, 1);
    });
    indexNotFounds.reverse();
    indexNotFounds.forEach((index) => {
      this.rowsForm.removeAt(index);
    });

    importedMeasurements.forEach((importMeasurementCSV: CsvMeasurementData) => {
      let importMeasurement: DeviceMeasurement = new DeviceMeasurement();
      const measurementImport: MeasurementImportCsv = importMeasurementCSV.measurement as MeasurementImportCsv;
      const measurementType: MeasurementType = this.measurementTypes.find(measurementType => measurementType.sensor.toLowerCase() === measurementImport.measurementType.toLowerCase());
        importMeasurement.name = measurementImport.name;
        importMeasurement.description = measurementImport.description;
        importMeasurement.alias = measurementImport.alias;
        importMeasurement.measurementType = measurementType;
        if (!isNaN(measurementImport.sampleRate)) {
          importMeasurement.sampleRate = measurementImport.sampleRate;
        }
      const newMeasurement = this.formGroupFromDeviceMeasurement(importMeasurement);
      this.rowsForm.push(newMeasurement);
      this.totalMeasures = this.totalMeasures + 1;
    });
    this.totalMeasures = this.rowsForm.length;

  }

  public async processFiles(file: File): Promise<boolean> {
    const filePromises: Promise<FileData> = this.readFile('id-' + (new Date()).getTime(), file);

    const _files: FileData = await filePromises;

    if (_files.fileErrors === null) {
      setTimeout(() => {
        this.importMeasurements(this.measurements);
      }, 500);

      return true;
    } else {
      return false;
    }
  }

  private readFile(fileIdentifier: string, file: File): Promise<FileData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const fileReader = reader.result.toString().split('\n');
          const fileErrors = this.processMeasurementFile(fileIdentifier, fileReader);
          const fileData: FileData = {
            fileIdentifier,
            file,
            fileErrors
          }
          resolve(fileData);
        } catch (err) {
          this.commonMessageService.showErrorUploadMeasurementsMessage();
          reject(err);
        }
      };
      reader.readAsText(file, 'ISO-8859-3');
      reader.onerror = (error) => {
        reject(error);
      };
    });
  }

  uploadFile(file: FileList): void {
    if (this.checkExtension(file[0].name)) {
      this.processFiles(file[0]).then(result => {
        if (result) {
          this.commonMessageService.showSuccessfullyUploadMeasurementsMessage();
        } else {
          this.commonMessageService.showErrorUploadMeasurementsMessage();
        }
      });
      this.attachments.nativeElement.value = '';
    }
  }

  checkExtension(fileName: string): boolean {
    let extension = fileName.split('.').pop();
    if (this.fileExtensions.includes(extension)) {
      return true;
    } else {
      this.commonMessageService.showUnexpectedPersistingError("create");
      return false;
    }
  }


  private processMeasurementFile(fileIdentifier: string, file: string[]): FileErrors | null {
    const measurements: CsvMeasurementData[] = [];
    let fileErrors: FileErrors = {
      lines: [],
    };

    file.forEach((line, lineIndex) => {
      if (line.trim() !== '' && !line.includes('Name')) {
        const rowData = line.replace('\r', '').split(';');
        if (rowData.length === 5) {
          const measurement = this.getMeasurementPersistRequestCsv(rowData);
          let csvMeasurementData: CsvMeasurementData = {
            measurement,
            fileIdentifier,
            status: 'NONE',
            lineIdentifier: 'none'
          };

          const errors = this.validateMeasurementImportData(csvMeasurementData);
          if (errors != null) {
            csvMeasurementData.status = 'ERROR';
            csvMeasurementData.errors = errors;
            fileErrors.lines.push(lineIndex - 1);

          } else {
            csvMeasurementData.status = 'CORRECT';
          }

          measurements.push(csvMeasurementData);
        } else {
          fileErrors.lines.push(lineIndex - 1);
        }
      }
    });
    this.fileFormatIncorrect = false;

    if (measurements.length > 0 && fileErrors.lines.length === 0) {
      this.measurements = measurements;
    }
    this.totalMeasurements = this.measurements.length;
    return fileErrors.lines.length > 0 ? fileErrors : null;
  }

  private getMeasurementPersistRequestCsv(rowData: string[]): MeasurementImportCsv {
    const [name, description, alias, measurementType, sampleRateStr] = rowData;
    const sampleRate: number = parseInt(sampleRateStr);
    return {name, description, alias, measurementType, sampleRate};
  }


  private validateMeasurementImportData(csvMeasurementData: CsvMeasurementData): CsvErrorList | null {
    const measurement = csvMeasurementData.measurement;
    const csvMeasurementErrors = new MeasurementCsvErrors(this.i18n);

    let present = csvMeasurementErrors.required(measurement, 'name');
    if (present) {
      csvMeasurementErrors.noSpecialCharacters(measurement, 'name');
      csvMeasurementErrors.maxLength(measurement, 'name', 255);
    }

    present = csvMeasurementErrors.required(measurement, 'measurementType');
    if (present) {
      csvMeasurementErrors.maxLength(measurement, 'measurementType', 255);
    }

    return Object.entries(csvMeasurementErrors.errors).length > 0 ? csvMeasurementErrors.errors : null;
  }

}
